import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Typography } from '@leapeasy/ui-kit';
import { DetailPageLoadingCenter } from 'components/DetailPageLoadingCenter';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AllApplicationsChart } from 'elements/Charts/AllApplicationsChart';
import { AppTypeChart } from 'elements/Charts/AppTypeChart';
import { getAppReportAction } from 'store/actions/reportActions';
import { navigatePoliciesFromDashboardFilter } from 'utils/policyDashboardNavigation';
import { AppPerformanceReview } from 'elements/Charts/AppPerformanceReview';
import { AppDistribution } from 'elements/Charts/AppDistribution';
import { PolicyIssuedChart } from 'elements/Charts/PolicyIssuedChart';
import { ExpiringPoliciesChart } from 'elements/Charts/ExpiringPoliciesChart';
import { CoverageChart } from 'elements/Charts/CoverageChart';
import { GrossRentChart } from 'elements/Charts/GrossRentChart';
import { ApplicationVolume6MonthsChart } from 'elements/Charts/ApplicationVolume6MonthsChart';
import { ApplicationStageChart } from 'elements/Charts/ApplicationStageChart';
import { DeclinesByReasonChart } from 'elements/Charts/DeclinesByReasonChart';
import { CancellationsByCancellationReasonChart } from 'elements/Charts/CancellationsByCancellationReasonChart';
import { navigateApplicationsFromDashboardFilter } from 'utils/navigateApplicationsFromDashboardFilter';
import { navigateClaimsFromDashboardFilter } from 'utils/navigateClaimsFromDashboardFilter';
import { TotalClaimChart } from 'elements/Charts/TotalClaimChart';
import { ClaimPaymentsChart } from 'elements/Charts/ClaimPaymentsChart';
import { ClaimFileStatusChart } from 'elements/Charts/ClaimFileStatusChart';

const VARIANTS = {
  /** Property → Building → Report (full charts, building-scoped Redux, rate row grid). */
  property_building_full: {
    appReportSliceKey: 'buildingAppReport',
    fetchType: 'building',
    chartSuite: 'full',
    showRateCards: true,
    rateCardsLayout: 'grid',
    waitForData: false,
  },
  /** Company / landlord detail Report tab (full charts, landlord-scoped Redux). */
  company_landlord_full: {
    appReportSliceKey: 'landlordAppReport',
    fetchType: 'landlord',
    chartSuite: 'full',
    showRateCards: false,
    waitForData: false,
  },
  /** Email- or user-by-role building context: two charts + policy + rate row, wait for report. */
  email_or_delegated_building_compact: {
    appReportSliceKey: 'buildingAppReport',
    fetchType: 'building',
    chartSuite: 'compact',
    showRateCards: true,
    rateCardsLayout: 'flex',
    waitForData: true,
  },
};

const RateCard = ({ item }) => (
  <Box
    width="20%"
    boxShadow="0 4px 8px rgba(0, 0, 0, 0.1)"
    background="#F7F7F9"
    borderRadius="12px"
    padding="17px"
    margin="10px"
  >
    <Box>
      <Typography variant="body2" style={{ padding: '6px' }}>
        {item.title}
      </Typography>
    </Box>
    <Box>
      <Typography variant="h1" style={{ color: '#4D1859', fontWeight: '700' }}>
        {item.rate}
      </Typography>
    </Box>
  </Box>
);

const resolveLandlordReportId = (building) =>
  building?.sf_landlord_id || building?.landlord_id || building?.id;

export const EntityReportTab = ({
  variant,
  building,
  onNavigateToApplications,
  /** When set, KPI / distribution / policy-issued drill-downs use this (e.g. building-scoped prefilter navigation). */
  onApplicationsDrilldown,
}) => {
  const config = useMemo(() => VARIANTS[variant] || null, [variant]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const reportData = useSelector((state) => {
    const c = VARIANTS[variant];
    return c ? state.getIn(['report', c.appReportSliceKey]) : null;
  });

  const navigatePoliciesFiltered = useCallback(
    (dashboardFilter) => {
      navigatePoliciesFromDashboardFilter(navigate, dashboardFilter);
    },
    [navigate],
  );

  const navigateApplicationsFiltered = useCallback(
    (dashboardFilter) => {
      navigateApplicationsFromDashboardFilter(navigate, dashboardFilter);
    },
    [navigate],
  );

  const navigateClaimsFiltered = useCallback(
    (claimsDashboardFilter) => {
      navigateClaimsFromDashboardFilter(navigate, claimsDashboardFilter);
    },
    [navigate],
  );

  const metricDrilldown = onApplicationsDrilldown || navigateApplicationsFiltered;

  const navigateApplicationsOverview = useCallback(() => {
    if (onNavigateToApplications) {
      onNavigateToApplications();
      return;
    }
    navigate('/property/applications');
  }, [onNavigateToApplications, navigate]);

  const navigatePoliciesOverview = useCallback(() => {
    navigate('/property/policies');
  }, [navigate]);

  useEffect(() => {
    if (!config) return;
    if (config.fetchType === 'building') {
      if (!building?.building_id) return;
      dispatch(getAppReportAction({ buildingId: building.building_id }));
    } else {
      const landlordId = resolveLandlordReportId(building);
      if (!landlordId) return;
      dispatch(getAppReportAction({ landlordId }));
    }
  }, [
    dispatch,
    config,
    building?.building_id,
    building?.sf_landlord_id,
    building?.landlord_id,
    building?.id,
  ]);

  const dataJS = useMemo(() => reportData?.toJS?.()?.data ?? null, [reportData]);

  const rateRows = useMemo(
    () => [
      {
        title: 'Property Approval Rate',
        rate: `${Number(building?.building_approval_rate).toFixed(2)}%`,
      },
      {
        title: 'Property Approval to Issue',
        rate: `${Number(building?.building_approved_to_issue_rate)?.toFixed(2)}%`,
      },
      {
        title: 'Total Decline Rate',
        rate: `${Number(building?.decline_percentage)?.toFixed(2)}%`,
      },
      {
        title: 'Total Cancellation Rate',
        rate: `${Number(building?.cancellation_percentage)?.toFixed(2)}%`,
      },
    ],
    [building],
  );

  if (!config) {
    return null;
  }

  if (!reportData) {
    return <DetailPageLoadingCenter minHeight={200} label="Loading report..." compact />;
  }

  const rateSection =
    config.showRateCards &&
    (config.rateCardsLayout === 'grid' ? (
      <RateCardContainer>
        {rateRows.map((row) => (
          <RateCard key={row.title} item={row} />
        ))}
      </RateCardContainer>
    ) : (
      <Box display="flex" justifyContent="space-between">
        {rateRows.map((row) => (
          <RateCard key={row.title} item={row} />
        ))}
      </Box>
    ));

  if (config.chartSuite === 'compact') {
    return (
      <>
        {rateSection}
        {reportData && dataJS && (
          <>
            <Box display="flex" justifyContent="space-between" gap="20px">
              <Box flex="1 1 0%">
                <AllApplicationsChart data={dataJS} onClickItem={onNavigateToApplications} />
              </Box>
              <Box flex="1 1 0%">
                <AppTypeChart data={dataJS} onClickItem={onNavigateToApplications} />
              </Box>
            </Box>
            <Box mt={2}>
              <PolicyIssuedChart data={dataJS} />
            </Box>
          </>
        )}
      </>
    );
  }

  // full suite
  return (
    <ReportTabContainer>
      {rateSection}
      <Box display="flex" justifyContent="space-between" gap="20px" flexDirection="column">
        {reportData && dataJS && (
          <React.Fragment>
            <Type1ChartContainer>
              <PirChartContainer>
                <AllApplicationsChart data={dataJS} onClickItem={onNavigateToApplications} />
              </PirChartContainer>
              <PirChartContainer>
                <AppTypeChart data={dataJS} onClickItem={onNavigateToApplications} />
              </PirChartContainer>
            </Type1ChartContainer>
            <PolicyIssuedChart
              data={dataJS}
              onPolicyIssuedDrilldown={
                onApplicationsDrilldown
                  ? () => onApplicationsDrilldown('Policy Issued - Stage 05')
                  : undefined
              }
            />
            <AppPerformanceReview data={dataJS} onApplicationsDrilldown={metricDrilldown} />
            <AppDistribution data={dataJS} onApplicationsDrilldown={metricDrilldown} />

            {dataJS?.claimCountsByMonth && (
              <React.Fragment>
                <TotalClaimChart
                  data={dataJS}
                  onDrilldownOpen={() => navigateClaimsFiltered('claims_summary_open')}
                  onDrilldownClosed={() => navigateClaimsFiltered('claims_summary_closed')}
                />
                <ClaimPaymentsChart
                  data={dataJS}
                  onDrilldownClosedBucket={() =>
                    navigateClaimsFiltered('claims_payment_closed_bucket')
                  }
                  onDrilldownPendingPayment={() => navigateClaimsFiltered('open_pending_payment')}
                  onDrilldownOther={() => navigateClaimsFiltered('claims_payment_other')}
                />
                <ClaimFileStatusChart
                  data={dataJS}
                  onClickItem={(key) => navigateClaimsFiltered(key)}
                />
              </React.Fragment>
            )}

            <Type2ChartContainer>
              <Box flex="1 1 0%">
                <ExpiringPoliciesChart
                  data={dataJS}
                  onClickItem={(rangeKey) => navigatePoliciesFiltered(rangeKey)}
                />
              </Box>
              <Box flex="1 1 0%">
                <CoverageChart
                  data={dataJS}
                  onClickItem={(coverageFilter) => navigatePoliciesFiltered(coverageFilter)}
                />
              </Box>
            </Type2ChartContainer>

            <GrossRentChart
              data={dataJS}
              onNavigateApplications={navigateApplicationsOverview}
              onNavigatePolicies={navigatePoliciesOverview}
            />
            <ApplicationVolume6MonthsChart
              data={dataJS}
              onNavigateApplications={navigateApplicationsOverview}
              onNavigatePolicies={navigatePoliciesOverview}
            />
            <ApplicationStageChart
              data={dataJS}
              onApplicationsDrilldown={(key) => metricDrilldown(key)}
            />
            <DeclinesByReasonChart
              data={dataJS}
              onDrilldown={() => metricDrilldown('06 - Policy Declined')}
            />
            <CancellationsByCancellationReasonChart
              data={dataJS}
              onDrilldown={() => metricDrilldown('Cancelled Application')}
            />
          </React.Fragment>
        )}
      </Box>
    </ReportTabContainer>
  );
};

const ReportTabContainer = styled.div`
  * {
    box-sizing: border-box;
  }
  box-sizing: border-box;
`;

const RateCardContainer = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, 1fr);
  width: 100%;
  @media screen and (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media screen and (max-width: 720px) {
    grid-template-columns: repeat(1, 1fr);
  }
  & > div {
    width: 95%;
  }
`;

// Wrap before sidebar-constrained content clips the pair.
const TYPE1_CHART_CARD_MIN_BASIS = '520px';

const Type1ChartContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 20px;
  width: 100%;
  min-width: 0;

  @media screen and (max-width: ${({ theme }) => theme['lg']}) {
    flex-direction: column;
  }
`;

const PirChartContainer = styled.div`
  flex: 1 1 ${TYPE1_CHART_CARD_MIN_BASIS};
  min-width: 0;
  max-width: 100%;
  & > div {
    & > div:nth-of-type(2) {
      @media screen and (max-width: ${({ theme }) => theme['md']}) {
        padding: 16px 6px !important;
        & > div {
          flex-direction: column !important;
          & > div:nth-of-type(2) {
            width: 100%;
            * {
              box-sizing: border-box;
            }
          }
        }
      }
    }

    & > div:nth-of-type(3) {
      @media screen and (max-width: ${({ theme }) => theme['md']}) {
        flex-direction: column;
      }
    }
  }
`;

const Type2ChartContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;

  @media screen and (max-width: ${({ theme }) => theme['xl']}) {
    flex-direction: column;
  }

  & > div:nth-of-type(1) {
    & > div {
      & > div:nth-of-type(3) {
        @media screen and (max-width: ${({ theme }) => theme['md']}) {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        @media screen and (max-width: ${({ theme }) => theme['sm']}) {
          grid-template-columns: repeat(1, 1fr);
        }
      }
    }
  }

  & > div:nth-of-type(2) {
    & > div {
      & > div:nth-of-type(2) {
        @media screen and (max-width: ${({ theme }) => theme['sm']}) {
          padding: 16px 6px !important;
        }
        & > div {
          @media screen and (max-width: ${({ theme }) => theme['sm']}) {
            flex-direction: column;
            * {
              box-sizing: border-box;
            }
            & > div:nth-of-type(2) {
              width: 100%;
            }
          }
        }
      }
      & > div:nth-of-type(3) {
        @media screen and (max-width: ${({ theme }) => theme['md']}) {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        @media screen and (max-width: ${({ theme }) => theme['sm']}) {
          grid-template-columns: repeat(1, 1fr);
        }
      }
    }
  }
`;

export const ENTITY_REPORT_VARIANT = {
  PROPERTY_BUILDING_FULL: 'property_building_full',
  COMPANY_LANDLORD_FULL: 'company_landlord_full',
  EMAIL_OR_DELEGATED_BUILDING_COMPACT: 'email_or_delegated_building_compact',
};
